import { useEffect } from 'react';

const LONG_PRESS_MS  = 400;
const LOCK_THRESHOLD = 8; // px before direction is decided

/**
 * Adds touch gestures to a chart wrapper:
 *   single-finger tap             → anchor crosshair at the tapped point
 *   single-finger vertical drag   → page scroll (pass through)
 *   single-finger horizontal drag → crosshair tooltip (persists on release)
 *   single-finger long press+drag → zoom selection
 *   two-finger horizontal drag    → pan (when onPanFtRef is provided)
 *   two-finger pinch/spread       → zoom out/in (when onPinchZoomRef is provided)
 *
 * All mutable values are accessed via refs so the effect only runs once.
 */
export function useTouchChartGestures({
  chartWrapRef,
  pixelToDistFtRef,     // ref to fn(clientX) → distFt | null
  onCrosshairMoveRef,   // ref to fn(clientX) → void
  onCrosshairClearRef,  // ref to fn() → void
  isSelectingRef,
  refAreaLeft,
  refAreaRight,
  setIsSelecting,
  setSelectionViz,
  finalizeZoomRef,
  isDirectHoverRef,     // optional
  onPanFtRef,           // optional — ref to fn(deltaFt) for two-finger pan
  onPinchZoomRef,       // optional — ref to fn(scaleFactor, centerFt) for pinch zoom
}) {
  useEffect(() => {
    const wrap = chartWrapRef.current;
    if (!wrap) return;

    let timer         = null;
    let mode          = null; // null | 'crosshair' | 'zoom' | 'scroll' | 'pan'
    let startX        = 0;
    let startY        = 0;
    let panPrevX      = 0;
    let pinchPrevDist = 0;
    let tapEligible   = false; // single finger down, no mode locked yet

    const armZoom = (clientX) => {
      wrap.style.touchAction = 'none';
      const distFt = pixelToDistFtRef.current(clientX);
      if (distFt == null) return;
      refAreaLeft.current    = distFt;
      refAreaRight.current   = distFt;
      isSelectingRef.current = true;
      setIsSelecting(true);
      setSelectionViz({ left: distFt, right: distFt });
    };

    const onTouchStart = (e) => {
      // Two-finger touch → pan + pinch zoom
      if (e.touches.length === 2 && (onPanFtRef || onPinchZoomRef)) {
        clearTimeout(timer);
        timer       = null;
        tapEligible = false;
        mode        = 'pan';
        panPrevX      = (e.touches[0].clientX + e.touches[1].clientX) / 2;
        pinchPrevDist = Math.hypot(
          e.touches[1].clientX - e.touches[0].clientX,
          e.touches[1].clientY - e.touches[0].clientY,
        );
        wrap.style.touchAction = 'none';
        return;
      }
      if (e.touches.length !== 1) return;

      startX = e.touches[0].clientX;
      startY = e.touches[0].clientY;
      mode   = null;
      // A stationary tap anchors the crosshair — but not when it lands on a
      // control inside the chart wrapper.
      tapEligible = !e.target?.closest?.('button, input, select, a');
      if (isDirectHoverRef) isDirectHoverRef.current = false;
      timer  = setTimeout(() => {
        mode = 'zoom';
        tapEligible = false;
        armZoom(startX);
      }, LONG_PRESS_MS);
    };

    const onTouchMove = (e) => {
      // Two-finger pan + pinch zoom
      if (mode === 'pan') {
        if (e.touches.length < 2) return;
        e.preventDefault();
        const currMidX = (e.touches[0].clientX + e.touches[1].clientX) / 2;
        const currDist = Math.hypot(
          e.touches[1].clientX - e.touches[0].clientX,
          e.touches[1].clientY - e.touches[0].clientY,
        );

        if (onPanFtRef) {
          const prevFt = pixelToDistFtRef.current(panPrevX);
          const currFt = pixelToDistFtRef.current(currMidX);
          if (prevFt != null && currFt != null) {
            // Drag right → see earlier data → negate delta
            onPanFtRef.current(-(currFt - prevFt));
          }
        }

        if (onPinchZoomRef && pinchPrevDist > 0) {
          const scaleFactor = currDist / pinchPrevDist;
          const centerFt    = pixelToDistFtRef.current(currMidX);
          onPinchZoomRef.current(scaleFactor, centerFt);
        }

        panPrevX      = currMidX;
        pinchPrevDist = currDist;
        return;
      }

      const touch = e.touches[0];

      if (mode === null) {
        const dx = Math.abs(touch.clientX - startX);
        const dy = Math.abs(touch.clientY - startY);
        if (dx < LOCK_THRESHOLD && dy < LOCK_THRESHOLD) return;
        clearTimeout(timer);
        timer = null;
        tapEligible = false;
        if (dx >= dy) {
          mode = 'crosshair';
          if (isDirectHoverRef) isDirectHoverRef.current = true;
          wrap.style.touchAction = 'none';
        } else {
          mode = 'scroll';
        }
      }

      if (mode === 'zoom') {
        e.preventDefault();
        const distFt = pixelToDistFtRef.current(touch.clientX);
        if (distFt == null) return;
        refAreaRight.current = distFt;
        setSelectionViz({ left: refAreaLeft.current, right: distFt });
      } else if (mode === 'crosshair') {
        e.preventDefault();
        onCrosshairMoveRef.current(touch.clientX);
      }
      // 'scroll': fall through — browser handles it
    };

    const onTouchEnd = () => {
      clearTimeout(timer);
      timer = null;
      const endMode = mode;
      const wasTap  = tapEligible;
      mode = null;
      tapEligible = false;
      wrap.style.touchAction = '';

      if (endMode === 'zoom') {
        finalizeZoomRef.current?.();
      } else if (endMode === 'crosshair') {
        // Keep the crosshair where the drag ended: on touch there is no
        // "mouse leave", and the anchored point carries to other panels
        // (they seed from the cursor cache when they mount).
        if (isDirectHoverRef) isDirectHoverRef.current = false;
      } else if (endMode === null && wasTap) {
        // Stationary tap → anchor the crosshair at the tapped point.
        onCrosshairMoveRef.current(startX);
      }
      // 'pan': no cleanup needed
    };

    const onTouchCancel = () => {
      clearTimeout(timer);
      timer = null;
      if (mode === 'zoom') {
        isSelectingRef.current = false;
        setIsSelecting(false);
        setSelectionViz(null);
        refAreaLeft.current  = null;
        refAreaRight.current = null;
      } else if (mode === 'crosshair') {
        if (isDirectHoverRef) isDirectHoverRef.current = false;
        onCrosshairClearRef.current();
      }
      mode = null;
      wrap.style.touchAction = '';
    };

    wrap.addEventListener('touchstart',  onTouchStart,  { passive: true });
    wrap.addEventListener('touchmove',   onTouchMove,   { passive: false });
    wrap.addEventListener('touchend',    onTouchEnd);
    wrap.addEventListener('touchcancel', onTouchCancel);
    return () => {
      wrap.removeEventListener('touchstart',  onTouchStart);
      wrap.removeEventListener('touchmove',   onTouchMove);
      wrap.removeEventListener('touchend',    onTouchEnd);
      wrap.removeEventListener('touchcancel', onTouchCancel);
      clearTimeout(timer);
    };
  }, []); // eslint-disable-line react-hooks/exhaustive-deps
}
